/**
 * Ability-contract checks — folded in from the former standalone
 * AbilityContractsDashboard (2026-06-10). That window was unreachable (no open
 * trigger) and a near-duplicate sibling of this dashboard ("same pattern, but
 * abilities"), so its validation lives here now as ordinary categories:
 *   - one "Ability System Health" category (DamageBridge listeners, critical
 *     services, dead combat events)
 *   - one category per class, each ability rolled up to a single pass/warn/fail
 *     check (its failing sub-checks summarized in the detail string)
 *
 * Same per-ability rules as before: function registered, metadata, balance
 * (mana/cooldown), behaviors, projectile config, VFX coverage, hotbar slot.
 */
import type { Category } from '../types';
import type { Check, Status } from '../../DashboardShared';
import { abilityRegistry } from '../../../../systems/player/abilities/AbilityRegistry';
import { DEFAULT_HOTBAR_SLOTS, UNLOCKABLE_ABILITIES } from '../../../../../shared/constants/DefaultHotbars';
import { DEFAULT_VFX_EFFECTS } from '../../../../effects/config/VFXEffectDefs';
import { ABILITY_VFX_EFFECTS } from '../../../../effects/config/VFXAbilityDefs';
import { EventBus } from '../../../../../shared/EventBus';
import { ServiceRegistry } from '../../../../core/ServiceRegistry';

// VFX lookup from definition arrays (available at import time, before VFXEffectPlayer boots).
const ALL_VFX_IDS = new Set([
  ...DEFAULT_VFX_EFFECTS.map(e => e.id),
  ...ABILITY_VFX_EFFECTS.map(e => e.id),
]);

const CLASS_ORDER = ['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid', 'dev', 'other'] as const;
const CLASS_TAGS = ['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid', 'dev'];
const CLASS_ICONS: Record<string, string> = {
  warrior: '⚔️',
  mage: '🧙',
  hunter: '🏹',
  paladin: '🛡️',
  rogue: '🗡️',
  priest: '✨',
  dev: '🛠️',
  other: '❓',
};

function buildHotbarLookup(): Map<string, { className: string; slot: number }> {
  const lookup = new Map<string, { className: string; slot: number }>();
  for (const [className, slots] of Object.entries(DEFAULT_HOTBAR_SLOTS)) {
    for (const slot of slots) {
      if (slot.abilityId) lookup.set(slot.abilityId, { className, slot: slot.slot });
    }
  }
  return lookup;
}

/** The per-ability sub-checks (function/metadata/balance/behaviors/projectile/vfx/hotbar). */
function abilitySubChecks(id: string, hotbarLookup: Map<string, { className: string; slot: number }>): {
  checks: Check[]; name: string; hotbarSlot?: number;
} {
  const meta = abilityRegistry.getMetadata(id);
  const fn = abilityRegistry.get(id);
  const checks: Check[] = [];
  const isAuto = id.endsWith('_auto');

  checks.push({ id: 'fn', label: 'Function', status: fn ? 'pass' : 'fail', detail: fn ? 'registered' : 'MISSING' });

  const hasName = !!meta?.name;
  const hasDesc = !!meta?.description;
  checks.push({
    id: 'meta', label: 'Metadata',
    status: hasName ? 'pass' : 'warn',
    detail: [hasName ? meta!.name : 'no name', hasDesc ? '' : 'no desc'].filter(Boolean).join(', ') || meta?.name || id,
  });

  const explicitlyPassive = (meta as any)?.isPassive === true;
  const autoPassive = !isAuto && (meta?.manaCost ?? 0) === 0 && (meta?.cooldown ?? 0) === 0;
  const isPassive = explicitlyPassive || autoPassive;
  if (isPassive) {
    checks.push({ id: 'balance', label: 'Balance', status: 'pass', detail: explicitlyPassive ? 'passive ability' : 'passive (no mana/cd)' });
  } else if (!isAuto) {
    const hasMana = (meta?.manaCost ?? 0) > 0;
    const hasCooldown = (meta?.cooldown ?? 0) > 0;
    const isFreeAbility = meta?.manaCost === 0 && hasCooldown;
    checks.push({
      id: 'balance', label: 'Balance',
      status: (hasMana && hasCooldown) || isFreeAbility ? 'pass' : hasMana || hasCooldown ? 'warn' : 'fail',
      detail: isFreeAbility ? `free cd:${meta?.cooldown}ms` : `mana:${meta?.manaCost ?? 0} cd:${meta?.cooldown ?? 0}ms`,
    });
  } else {
    checks.push({ id: 'balance', label: 'Balance', status: 'pass', detail: `auto-attack (mana:${meta?.manaCost ?? 0})` });
  }

  const behaviors = meta?.behaviors || [];
  const hasProjectile = !!meta?.projectile;
  const hasMelee = !!meta?.melee;
  const hasBehaviors = behaviors.length > 0 || hasProjectile || hasMelee;
  checks.push({
    id: 'behaviors', label: 'Behaviors',
    status: hasBehaviors ? 'pass' : 'fail',
    detail: hasBehaviors
      ? [hasProjectile && 'projectile', hasMelee && 'melee', ...behaviors.map(b => b.type)].filter(Boolean).join(', ')
      : 'NONE',
  });

  if (hasProjectile) {
    const p = meta!.projectile!;
    const complete = !!p.shape && p.color != null && (p.speed ?? 0) > 0 && p.damage != null;
    checks.push({
      id: 'projectile', label: 'Projectile',
      status: complete ? 'pass' : 'warn',
      detail: `${p.shape || '?'} ${typeof p.color === 'number' ? '#' + p.color.toString(16).padStart(6, '0') : '?'} spd:${p.speed ?? '?'} dmg:${typeof p.damage === 'number' ? p.damage : 'fn'}`,
    });
  }

  const allVfxIds = abilityRegistry.vfxIds(id);
  const registeredVfx = allVfxIds.filter(vid => ALL_VFX_IDS.has(vid));
  checks.push({
    id: 'vfx', label: 'VFX',
    status: allVfxIds.length > 0 ? (registeredVfx.length === allVfxIds.length ? 'pass' : 'warn') : 'warn',
    detail: allVfxIds.length > 0 ? `${registeredVfx.length}/${allVfxIds.length} defined (${allVfxIds.join(', ')})` : 'none configured',
  });

  const hotbarInfo = hotbarLookup.get(id);
  const isUnlockable = Object.values(UNLOCKABLE_ABILITIES).some(arr => arr.includes(id));
  checks.push({
    id: 'hotbar', label: 'Hotbar',
    status: hotbarInfo || isUnlockable ? 'pass' : 'warn',
    detail: hotbarInfo ? `${hotbarInfo.className} slot ${hotbarInfo.slot}` : isUnlockable ? 'unlockable (not default)' : 'not on any default hotbar',
  });

  return { checks, name: meta?.name || id, hotbarSlot: hotbarInfo?.slot };
}

/** Runtime ability-system health (only meaningful once the game scene is loaded). */
function checkSystemHealth(): Check[] {
  const bus = EventBus.getInstance();
  const registry = ServiceRegistry.getInstance();
  const isGameSceneLoaded = registry.has('EntitySystem') || registry.has('PhysicsSystem');
  if (!isGameSceneLoaded) {
    return [{ id: 'scene_not_loaded', label: 'Runtime checks', status: 'pass', detail: 'Game scene not loaded — runtime checks deferred' }];
  }

  const checks: Check[] = [];
  const meleeListeners = bus.listenerCount('combat:melee_hit');
  const areaListeners = bus.listenerCount('combat:area_hit');
  checks.push({ id: 'damage_bridge_melee', label: 'DamageBridge melee_hit', status: meleeListeners > 0 ? 'pass' : 'fail', detail: meleeListeners > 0 ? `${meleeListeners} listener(s)` : 'NO LISTENERS — melee deals 0 server damage' });
  checks.push({ id: 'damage_bridge_area', label: 'DamageBridge area_hit', status: areaListeners > 0 ? 'pass' : 'fail', detail: areaListeners > 0 ? `${areaListeners} listener(s)` : 'NO LISTENERS — AOE deals 0 server damage' });

  const entitySystem = registry.has('EntitySystem');
  const projectileSystem = registry.has('ProjectileSystem');
  const castTimeline = registry.has('CastTimelineSystem');
  checks.push({ id: 'entity_system', label: 'EntitySystem', status: entitySystem ? 'pass' : 'fail', detail: entitySystem ? 'registered' : 'MISSING — all abilities will fail' });
  checks.push({ id: 'projectile_system', label: 'ProjectileSystem', status: projectileSystem ? 'pass' : 'fail', detail: projectileSystem ? 'registered' : 'MISSING — projectile abilities will fail' });
  checks.push({ id: 'cast_timeline', label: 'CastTimelineSystem', status: castTimeline ? 'pass' : 'fail', detail: castTimeline ? 'registered' : 'MISSING — timed abilities will fail' });

  const combatDead = bus.getDeadEvents().filter(e => e.event.startsWith('combat:'));
  checks.push({ id: 'dead_combat_events', label: 'Dead combat events', status: combatDead.length === 0 ? 'pass' : 'fail', detail: combatDead.length === 0 ? 'none' : combatDead.map(e => e.event).join(', ') });

  return checks;
}

/** Roll the worst sub-check status up to the ability's row status. */
function rollUp(checks: Check[]): Status {
  if (checks.some(c => c.status === 'fail')) return 'fail';
  if (checks.some(c => c.status === 'warn')) return 'warn';
  return 'pass';
}

/**
 * Ability-contract categories for the unified Contract Dashboard: system health
 * + one category per class (each ability = one rolled-up check).
 */
export function checkAbilityContracts(): Category[] {
  const hotbarLookup = buildHotbarLookup();
  const categories: Category[] = [
    { id: 'ability-system-health', label: 'Ability System Health', icon: '❤️', checks: checkSystemHealth() },
  ];

  const byClass = new Map<string, Check[]>();
  for (const ability of abilityRegistry.getAll()) {
    const { checks, name, hotbarSlot } = abilitySubChecks(ability.id, hotbarLookup);
    const problems = checks.filter(c => c.status !== 'pass').map(c => `${c.label}:${c.detail}`);
    const cls = ability.tags?.find(t => CLASS_TAGS.includes(t)) || 'other';
    const check: Check = {
      id: `ability_${ability.id}`,
      label: name,
      status: rollUp(checks),
      detail: problems.length
        ? problems.join(' · ')
        : `${ability.id}${hotbarSlot != null ? ` · slot ${hotbarSlot}` : ''} — all checks pass`,
    };
    if (!byClass.has(cls)) byClass.set(cls, []);
    byClass.get(cls)!.push(check);
  }

  for (const cls of CLASS_ORDER) {
    const checks = byClass.get(cls);
    if (checks && checks.length) {
      categories.push({
        id: `abilities-${cls}`,
        label: `${cls[0].toUpperCase()}${cls.slice(1)} Abilities`,
        icon: CLASS_ICONS[cls] || '❓',
        checks,
      });
    }
  }

  return categories;
}
